import { useCallback } from 'react';
import { type RoutePath, type RouteSegments, useNavigationContainerRef } from 'expo-router';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns a function that resets the nested Menu navigator to a typed screen and
 * reports whether the Menu navigator was available to reset.
 */
export const useResetMenuStack = () => {
  const navigationRef = useNavigationContainerRef();

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const resetMenuStack = useCallback(
    (params: ResetMenuStackParams) => {
      const { screenName, params: routeParams } = params;

      const menuNavigationState = findNavigationState({
        navigationState: navigationRef.getRootState(),
        routeName: MENU_TAB_ROUTE,
      });

      if (!menuNavigationState?.key) {
        return false;
      }

      // Expo Router ignores partial RESET payloads, so keep the navigator's own key,
      // route names and key sequence from its current state.
      navigationRef.dispatch({
        type: 'RESET',
        target: menuNavigationState.key,
        payload: {
          ...menuNavigationState,
          index: 0,
          routes: [{ name: screenName, params: routeParams }],
          stale: false,
        },
      } as never);

      return true;
    },
    [navigationRef],
  );

  return resetMenuStack;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Searches from the last route, so when a link from a modal stacks another `(tabs)`
 * on the root, the visible Menu navigator wins over the hidden one below it.
 */
function findNavigationState(params: FindNavigationStateParams): NavigationState | undefined {
  const { navigationState, routeName } = params;

  return navigationState.routes.reduceRight<NavigationState | undefined>((matchingState, route) => {
    if (matchingState) {
      return matchingState;
    }

    if (route.name === routeName && route.state?.key) {
      return route.state;
    }

    if (!route.state) {
      return matchingState;
    }

    return findNavigationState({ navigationState: route.state, routeName });
  }, undefined);
}

// ─── Constants ───────────────────────────────────────────────────────────────

const MENU_TAB_ROUTE = '(menu)' satisfies RouteSegment;

// ─── Types ───────────────────────────────────────────────────────────────────

type ResetMenuStackParams = {
  screenName: StackRouteName<RoutePath>;
  params?: Record<string, unknown>;
};

type NavigationState = {
  key?: string;
  routes: readonly NavigationRoute[];
};

type NavigationRoute = {
  name: string;
  state?: NavigationState;
};

type FindNavigationStateParams = {
  navigationState: NavigationState;
  routeName: RouteSegment;
};

type RouteSegment = RouteSegments<RoutePath>[number];

type StackRouteName<Route extends RoutePath> = Route extends `/${infer Path}`
  ? `${Path}/index`
  : never;
